<template>
  <ActivityDetailDialog
    v-model:visible="detailDialogVisible"
    :all-user="allUser"
    :activity="detailedActivity!"
    :all-software-licenses="allSoftwareLicenses"
    :all-contracts="allContracts"
    :user-profiles="userProfiles" />

  <div v-loading="loading">
    <div
      v-for="(_activities, date) in dateSortedActivities"
      :key="date"
      class="day-activity">
      <h3>{{ translateDate(date) }}</h3>
      <el-timeline class="timeline">
        <el-timeline-item
          v-for="(activity, index) in _activities"
          :key="index"
          class="activity-item"
          :timestamp="dateToString(activity.created)"
          :color="index == 0 ? 'var(--sm-primary)' : undefined"
          placement="top"
          @click="handleActivityClick(activity)">
          <span>
            {{ activityMessages[activity._id] }}
          </span>
        </el-timeline-item>
      </el-timeline>
    </div>
  </div>
</template>

<script setup lang="ts">
  import dayjs from 'dayjs'
  import utc from 'dayjs/plugin/utc'
  import timezone from 'dayjs/plugin/timezone'
  import localizedFormat from 'dayjs/plugin/localizedFormat'
  import customParseFormat from 'dayjs/plugin/customParseFormat'
  dayjs.extend(utc)
  dayjs.extend(timezone)
  dayjs.extend(localizedFormat)
  dayjs.extend(customParseFormat)

  import { useI18n } from 'vue-i18n'

  import {
    Account,
    ActivityBaseModel,
    PriceInformation,
    SoftwareLicense,
    SoftwareLicensesService,
    UserProfilePublic,
  } from '@/client'
  import { getI18nArgs } from '@/utils/activity'
  import { logError } from '@/utils/errors'
  import ActivityDetailDialog from './ActivityDetailDialog.vue'
  import { displayDate } from '@/utils/formatter/date'
  import { PersonStore } from '@/stores/person/person.store'

  type SortedActivites = Record<string, ActivityBaseModel[]>

  const { t } = useI18n()
  const props = defineProps<{
    activities: ActivityBaseModel[]
    userProfiles: Record<string, UserProfilePublic>
  }>()

  const tz = dayjs.tz.guess()
  const toLocal = (v: string | number) => dayjs.utc(v).tz(tz)

  const allUser = ref<Account[]>([])
  const allSoftwareLicenses = ref<SoftwareLicense[]>([])
  const allContracts = ref<PriceInformation[]>([])
  const detailedActivity = ref<ActivityBaseModel | null>(null)
  const detailDialogVisible = ref(false)
  const loading = ref(true)

  const dateSortedActivities = computed<SortedActivites>(() => {
    const structured: SortedActivites = {}
    props.activities.forEach((a) => {
      if (!a.created) return
      const key = toLocal(a.created).format('DD.MM.YYYY')
      if (structured[key]) structured[key].push(a)
      else structured[key] = [a]
    })
    return structured
  })

  const dateToString = (date: string | number): string =>
    displayDate(toLocal(date).toDate(), { timeStyle: 'short' })

  function translateDate(date: string): string {
    const localDay = dayjs.tz(date, 'DD.MM.YYYY', tz)
    if (localDay.isSame(dayjs().tz(tz), 'day'))
      return t('views.dashboard.lastActivity.today')
    if (localDay.isSame(dayjs().tz(tz).subtract(1, 'day'), 'day'))
      return t('views.dashboard.lastActivity.yesterday')
    return displayDate(localDay.toDate(), { dateStyle: 'short' })
  }

  function handleActivityClick(activity: ActivityBaseModel) {
    detailedActivity.value = activity
    detailDialogVisible.value = true
  }

  // Fetching all needed resources ({includeDeleted: true} is necessary and not yet implemented in stores, to display emails of 'deleted' licenses, etc.)
  // Applying loading effect until all requests are finished
  onMounted(() => {
    const p: Promise<void | object>[] = []
    p.push(
      PersonStore.getPersons<Account>({ isDeleted: false }, {}, {}, false).then(
        (u) => {
          if (u) allUser.value = u
        }
      )
    )
    p.push(
      SoftwareLicensesService.getSoftwareLicensesApiV1SoftwareSoftwareLicensesGet(
        {
          includeDeleted: true,
        }
      ).then((s) => {
        if (s) allSoftwareLicenses.value = s
      })
    )
    p.push(
      SoftwareLicensesService.getSoftwareLicensePriceInformationsApiV1SoftwareSoftwareLicensePriceInformationsGet(
        { includeDeleted: true }
      ).then((contracts) => {
        if (contracts) allContracts.value = contracts
      })
    )
    Promise.all(p).finally(() => {
      loading.value = false
    })
  })

  // Load all activity i18n strings asynchronously
  const activityMessages: Ref<Record<string, string>> = ref({})

  async function getI18nString(activity: ActivityBaseModel) {
    const args = await getI18nArgs(
      activity,
      allUser.value,
      allSoftwareLicenses.value,
      allContracts.value,
      props.userProfiles[activity.identity_id]
    )
    return t(`activity.${activity.action}`, { ...args })
  }

  async function mapActivityMessages() {
    loading.value = true
    const translations: Record<string, string> = {}

    try {
      for (const activity of props.activities) {
        try {
          translations[activity._id] = await getI18nString(activity)
        } catch (error) {
          logError(
            error,
            'Failed to translate activity message',
            { activityId: activity._id, action: activity.action },
            { op: 'activity.mapActivityMessages' }
          )
          translations[activity._id] = '...'
        }
      }
      activityMessages.value = translations
    } finally {
      loading.value = false
    }
  }

  watch(() => props.activities, mapActivityMessages, { immediate: true })
</script>

<style scoped>
  .day-activity {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    justify-content: flex-start;
    justify-items: flex-start;
    margin-left: 5px;
  }

  .timeline {
    padding-left: 0;
  }

  .activity-item {
    padding-top: 8px;
    font-size: 0.9rem;
    cursor: pointer;
  }
</style>
